import { useMemo } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import { QRCodeComponent } from 'components/Common/QRCode'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { formatPhoneNumber } from 'lib/format/formatPhoneNumber'
import Image from 'components/Common/Image'
import { rem } from 'polished'

const StyledDivider = styled.hr`
  width: 100%;
  height: ${rem(2)};
  background-color: ${props => props.theme.palette.neutral.control.black100};
  border: none;
`
interface VoucherProps {
  address: string
  expiryDate: string
  voucher: App.ExperienceVoucher
}

type VoucherCodeProps = {
  number: string
  pin: string | undefined
}

export function PrintDisneyCaliforniaVoucher(props: VoucherProps) {
  const intl = useIntl()
  const { voucher } = props
  const supportPhoneNumber = config.PRIORITY_SUPPORT_NUMBER_SUPPORT_ASSISTANT

  const voucherCode = useMemo((): VoucherCodeProps => {
    const code = voucher.voucherCode.split('-')
    return { number: code[0], pin: code[1] ?? undefined }
  }, [voucher])

  return (
    <LayoutContainer variant="large">
      <Group direction="vertical" horizontalAlign="center" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
          <Group direction="horizontal">
            <Image id="dlr_logo_djg5" width="350" />
          </Group>
          <Group direction="vertical" horizontalAlign="center" gap={8}>
            <QRCodeComponent size={110} value={voucherCode.number} />
            <BodyText variant="medium" colour="light-foreground-alt">{voucherCode.number}</BodyText>
          </Group>
        </Group>

        <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading2">
              {intl.formatMessage({
                id: 'qQF97ukwUoEky-assAXj',
                defaultMessage: 'Disney California Adventure eTicket',
                description: 'Heading for Disney California Adventure electronic ticket voucher',
              })}
            </Heading>
            <BodyText weight="bold" variant="large">
              {voucher.offerPackage.name} {voucher.fareType}
            </BodyText>
          </Group>

          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" gap={2}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'MMtlNQDv4T0Sxh0RrPmT',
                  defaultMessage: 'Order Reference ID:',
                  description: 'Label for order reference ID on Disney California voucher',
                })}
              </BodyText>
              <BodyText variant="medium">{voucher.orderNumber}</BodyText>
            </Group>

            <Group direction="horizontal" gap={2}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'OESNLA6opCEEsZdisZJ2',
                  defaultMessage: 'Disney Order ID:',
                  description: 'Label for Disney order ID on Disney California voucher',
                })}
              </BodyText>
              <BodyText variant="medium">{voucher.providerItemId}</BodyText>
            </Group>

            <Group direction="horizontal" gap={2}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'gZzOHht8eyIcmuSJrv9Z',
                  defaultMessage: 'Guest Name:',
                  description: 'Form field label for guest name on Disney California voucher',
                })}
              </BodyText>
              <BodyText variant="medium">{voucher.customer?.name} {voucher.customer?.surname}</BodyText>
            </Group>

            <Group direction="horizontal" gap={2}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'r_uRHLwYrHEc24xKam7d',
                  defaultMessage: 'Ticket #:',
                  description: 'Label for ticket number on Disney California voucher',
                })}
              </BodyText>
              <BodyText variant="medium">{voucherCode.number}</BodyText>
            </Group>
          </Group>
        </Group>

        <Group direction="horizontal" horizontalAlign="space-between" fullWidth>
          <MarkdownRender
            content={voucher.policies}
            fontSize="small"
          />
        </Group>

        <StyledDivider />

        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end" fullWidth>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium" weight="bold">{intl.formatMessage({
              id: '11MdloXnrGQsBQa8q763',
              defaultMessage: 'LUXURY ESCAPES',
              description: 'Brand name displayed on Disney California experience voucher',
            })}
            </BodyText>
            <BodyText variant="medium">{formatPhoneNumber(supportPhoneNumber, '+61')}</BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end" gap={8}>
            <LogoIcon />
            <BodyText variant="medium">
              {intl.formatMessage(
                {
                  id: 'O720GC7TdzaFsdo_gcNC',
                  defaultMessage: 'TICKET {voucherIndex} OF {voucherAmount}',
                  description: 'Ticket number indicator showing current ticket position out of total on Disney California voucher',
                },
                { voucherIndex: voucher.voucherIndex, voucherAmount: voucher.voucherAmount },
              )}
            </BodyText>
          </Group>
        </Group>
      </Group>
    </LayoutContainer>
  )
}
